/**
 * useMultipartUpload — custom hook encapsulating all S3 Multipart Upload logic.
 *
 * Requirements: 2.2, 2.3, 18.1, 18.5
 */

import toast from 'react-hot-toast';
import { MultipartUploadOptions, ResumeCheckpoint, UseMultipartUploadReturn } from '../types/upload';
import { apiCall } from '../services/api';

// ---------- Internal constants ----------

const PART_SIZE = 8_388_608;        // 8 MiB
const MAX_CONCURRENT_PARTS = 3;
const MAX_RETRIES = 3;
const RETRY_DELAYS_MS: number[] = [1000, 2000, 4000];

// ---------- Checkpoint helpers (module-level for testability) ----------

// Requirements: 5.1, 5.2, 5.3, 5.4

/**
 * Returns the sessionStorage key for a given bucket/key pair.
 * Pattern: mpu:${bucket}:${encodeURIComponent(key)}
 */
function checkpointKey(bucket: string, key: string): string {
  return `mpu:${bucket}:${encodeURIComponent(key)}`;
}

/**
 * Writes a full checkpoint to sessionStorage and localStorage.
 * Fails silently if either store is unavailable.
 */
function writeCheckpoint(checkpoint: ResumeCheckpoint): void {
  const ck = checkpointKey(checkpoint.bucket, checkpoint.key);
  const serialised = JSON.stringify(checkpoint);
  try {
    sessionStorage.setItem(ck, serialised);
  } catch {
    // sessionStorage may be unavailable; fail silently
  }
  try {
    localStorage.setItem(ck, serialised);
  } catch {
    // localStorage may be unavailable (private browsing, quota); fail silently
  }
}

/**
 * Updates a single completed part (partNumber → ETag) in an existing checkpoint.
 * Mirrors the update to localStorage if an entry already exists there.
 * Fails silently if the checkpoint does not exist or either store is unavailable.
 */
function updateCheckpoint(bucket: string, key: string, partNumber: number, etag: string): void {
  const ck = checkpointKey(bucket, key);
  try {
    const raw = sessionStorage.getItem(ck);
    if (!raw) return;
    const checkpoint: ResumeCheckpoint = JSON.parse(raw);
    checkpoint.completedParts[partNumber] = etag;
    const serialised = JSON.stringify(checkpoint);
    sessionStorage.setItem(ck, serialised);
    // Mirror to localStorage so the checkpoint survives a page reload
    try {
      const lsRaw = localStorage.getItem(ck);
      if (lsRaw) {
        localStorage.setItem(ck, serialised);
      }
    } catch {
      // localStorage may be unavailable; fail silently
    }
  } catch {
    // fail silently
  }
}

/**
 * Removes the checkpoint for the given bucket/key from both sessionStorage and localStorage.
 * Fails silently if either store is unavailable.
 */
function removeCheckpoint(bucket: string, key: string): void {
  const ck = checkpointKey(bucket, key);
  try {
    sessionStorage.removeItem(ck);
  } catch {
    // fail silently
  }
  try {
    localStorage.removeItem(ck);
  } catch {
    // localStorage may be unavailable; fail silently
  }
}

// ---------- Single-part XHR upload with retry ----------

/**
 * Uploads a single file slice to a presigned S3 URL via XHR.
 * Retries up to MAX_RETRIES times with exponential backoff on failure.
 * Returns the ETag from the S3 response.
 * Requirements: 2.4, 2.6, 2.7, 2.8, 4.3
 */
async function uploadPartWithRetry(
  url: string,
  slice: Blob,
  partNumber: number,
  signal: AbortSignal,
  onProgress: (partNumber: number, loaded: number, total: number) => void
): Promise<string> {
  let lastError: Error = new Error('Upload failed');

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    if (attempt > 0) {
      await new Promise<void>((resolve) => setTimeout(resolve, RETRY_DELAYS_MS[attempt - 1]));
    }
    if (signal.aborted) throw new Error('Upload aborted');

    try {
      const etag = await new Promise<string>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const onAbort = () => { xhr.abort(); reject(new Error('Upload aborted')); };
        signal.addEventListener('abort', onAbort, { once: true });

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) onProgress(partNumber, event.loaded, event.total);
        };
        xhr.onload = () => {
          signal.removeEventListener('abort', onAbort);
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(xhr.getResponseHeader('ETag') || '');
          } else {
            reject(new Error(`Part ${partNumber} upload failed with status ${xhr.status}`));
          }
        };
        xhr.onerror = () => {
          signal.removeEventListener('abort', onAbort);
          reject(new Error(`Part ${partNumber} network error`));
        };
        xhr.onabort = () => {
          signal.removeEventListener('abort', onAbort);
          reject(new Error('Upload aborted'));
        };
        // Do NOT set Content-Type — S3 presigned URLs reject unexpected headers (Req 2.8)
        xhr.open('PUT', url);
        xhr.send(slice);
      });
      return etag;
    } catch (err) {
      // If aborted, propagate immediately without retrying
      if (signal.aborted || (err instanceof Error && err.message === 'Upload aborted')) {
        throw err;
      }
      lastError = err instanceof Error ? err : new Error(String(err));
      // If this was the last attempt, fall through to throw below
      if (attempt === MAX_RETRIES) break;
    }
  }

  throw lastError;
}

// ---------- Hook ----------

export function useMultipartUpload(): UseMultipartUploadReturn {
  const uploadMultipart = async (options: MultipartUploadOptions): Promise<void> => {
    const { bucketName, key, file, abortSignal, onPartProgress, resumeCheckpoint } = options;
    const { registerTransfer } = options;
    const fileSize = file.size;

    // ----------------------------------------------------------------
    // Step 1: Initiate or resume
    // ----------------------------------------------------------------
    let uploadId: string;
    let totalParts: number;
    let completedParts: Record<number, string>;

    if (!resumeCheckpoint) {
      // New upload — call initiate endpoint
      let initiateResponse: { uploadId: string; key: string };
      try {
        initiateResponse = await apiCall(`/s3/multipart-initiate/${bucketName}`, {
          method: 'POST',
          body: JSON.stringify({ fileName: file.name, fileSize }),
        });
      } catch (err) {
        // Duplicate-name conflict (409): the backend blocks the upload when an
        // object with the same key already exists in the folder. Surface the
        // specific reason instead of the generic failure message.
        const message = err instanceof Error ? err.message : '';
        if (message.toLowerCase().includes('already exists')) {
          toast.error(`${file.name}: a file with this name already exists in this folder`);
        } else {
          toast.error('Failed to start upload');
        }
        return; // Do NOT write checkpoint on initiation failure (Req 5.5)
      }

      uploadId = initiateResponse.uploadId;
      totalParts = Math.ceil(fileSize / PART_SIZE);
      completedParts = {};
    } else {
      // Resuming — use existing checkpoint state as starting point
      uploadId = resumeCheckpoint.uploadId;
      totalParts = resumeCheckpoint.totalParts;
      completedParts = { ...resumeCheckpoint.completedParts };

      // Reconcile with S3: call ListParts to discover the true watermark.
      // Parts uploaded between the last checkpoint write and now may not be
      // reflected in the local snapshot. S3 is authoritative on ETags.
      // Requirements: 2.3, 2.4, 2.5
      try {
        const listResult: { parts: Array<{ partNumber: number; etag: string }> } =
          await apiCall(
            `/s3/multipart-list-parts/${bucketName}?uploadId=${encodeURIComponent(uploadId)}&key=${encodeURIComponent(key)}`
          );
        // Merge: union of local and S3 parts; S3 ETag wins on conflict
        for (const part of listResult.parts) {
          completedParts[part.partNumber] = part.etag;
        }
        // Persist reconciled state to both stores before uploading any parts
        writeCheckpoint({
          uploadId,
          bucket: bucketName,
          key,
          totalParts,
          partSize: PART_SIZE,
          fileSize,
          completedParts,
          initiatedAt: resumeCheckpoint.initiatedAt,
        });
      } catch (err) {
        // 404 = upload session expired on S3 — must restart with a fresh uploadId.
        // The backend returns 'Upload session not found or expired' for NoSuchUpload.
        if (err instanceof Error && err.message.includes('Upload session not found')) {
          // Upload session no longer exists on S3; restart from scratch
          try {
            const initiateResponse: { uploadId: string; key: string } = await apiCall(
              `/s3/multipart-initiate/${bucketName}`,
              { method: 'POST', body: JSON.stringify({ fileName: key.split('/').pop() || key, fileSize }) }
            );
            uploadId = initiateResponse.uploadId;
            completedParts = {};
            // writeCheckpoint will be called in Step 2 below with the fresh uploadId
          } catch {
            toast.error('Failed to restart expired upload');
            return;
          }
        }
        // For all other errors (network, auth, unexpected): proceed with local
        // snapshot. This is best-effort — better to retry with some overlap than fail.
      }
    }

    // Register with TransferContext for the duration of the upload (Req 3.3, 3.4)
    const cleanupTransfer: () => void = registerTransfer?.({
      id: `${bucketName}:${key}:${uploadId}`,
      type: 'upload',
      bucket: bucketName,
      key,
    }) ?? (() => {});

    try {

    // ----------------------------------------------------------------
    // Step 2: Write initial checkpoint (Req 5.1)
    // ----------------------------------------------------------------
    writeCheckpoint({
      uploadId,
      bucket: bucketName,
      key,
      totalParts,
      partSize: PART_SIZE,
      fileSize,
      completedParts: resumeCheckpoint?.completedParts ?? {},
      initiatedAt: resumeCheckpoint ? resumeCheckpoint.initiatedAt : new Date().toISOString(),
    });

    // ----------------------------------------------------------------
    // Step 3: Compute parts still needing upload
    // ----------------------------------------------------------------
    const allPartNumbers = Array.from({ length: totalParts }, (_, i) => i + 1);
    const remainingParts = allPartNumbers.filter((n) => !(n in completedParts));

    // ----------------------------------------------------------------
    // Step 4: Batch upload with concurrency
    // ----------------------------------------------------------------
    let uploadError: Error | null = null;

    for (let batchStart = 0; batchStart < remainingParts.length; batchStart += MAX_CONCURRENT_PARTS) {
      // Step 5: Check abort before dispatching a new batch (Req 4.6, 2.7)
      if (abortSignal.aborted) {
        removeCheckpoint(bucketName, key);
        return;
      }

      const batchPartNumbers = remainingParts.slice(batchStart, batchStart + MAX_CONCURRENT_PARTS);

      // Fetch presigned URLs for this batch
      let presignedUrls: Record<string, string>;
      try {
        const presignResponse: { presignedUrls: Record<string, string> } = await apiCall(
          `/s3/multipart-presign/${bucketName}`,
          {
            method: 'POST',
            body: JSON.stringify({ uploadId, key, partNumbers: batchPartNumbers }),
          }
        );
        presignedUrls = presignResponse.presignedUrls;
      } catch (err) {
        // Presign failure counts as a part failure — treat as retry exhaustion
        uploadError = err instanceof Error ? err : new Error('Failed to get presigned URLs');
        break;
      }

      // Upload all parts in this batch concurrently; collect results/errors
      const batchPromises = batchPartNumbers.map(async (partNumber) => {
        const start = (partNumber - 1) * PART_SIZE;
        const end = Math.min(partNumber * PART_SIZE, fileSize);
        const slice = file.slice(start, end);
        const url = presignedUrls[String(partNumber)];

        const etag = await uploadPartWithRetry(url, slice, partNumber, abortSignal, onPartProgress);
        return { partNumber, etag };
      });

      // Wait for all parts in batch to settle (Req 2.5 — let in-flight complete)
      const results = await Promise.allSettled(batchPromises);

      for (const result of results) {
        if (result.status === 'fulfilled') {
          const { partNumber, etag } = result.value;
          completedParts[partNumber] = etag;
          updateCheckpoint(bucketName, key, partNumber, etag); // Req 5.2
          onPartProgress(partNumber, PART_SIZE, PART_SIZE); // signal completion
        } else {
          // Step 6: Part retry exhaustion — check if user-aborted or real failure
          const err = result.reason instanceof Error ? result.reason : new Error('Part upload failed');
          if (!abortSignal.aborted && err.message !== 'Upload aborted') {
            uploadError = err;
          }
          // If signal is aborted, we'll handle it at top of the next loop iteration
        }
      }

      if (uploadError !== null) {
        break; // Exit batch loop; proceed to abort/cleanup below
      }

      // Re-check abort after the batch completes (user may have aborted during the batch)
      if (abortSignal.aborted) {
        removeCheckpoint(bucketName, key);
        return;
      }
    }

    // ----------------------------------------------------------------
    // Step 6 (continued): Handle retry exhaustion (NOT user abort)
    // ----------------------------------------------------------------
    if (uploadError !== null) {
      // Call DELETE /s3/multipart-abort/{bucket} — ignore failures (Req 5.7, 2.5)
      try {
        await apiCall(`/s3/multipart-abort/${bucketName}`, {
          method: 'DELETE',
          body: JSON.stringify({ uploadId, key }),
        });
      } catch (abortErr) {
        console.error('Failed to abort multipart upload:', abortErr);
      }

      removeCheckpoint(bucketName, key); // Req 5.7
      toast.error(uploadError.message || 'Upload failed');
      return;
    }

    // ----------------------------------------------------------------
    // Step 7: Complete the multipart upload (Req 2.9, 5.3, 5.6)
    // ----------------------------------------------------------------
    const sortedParts = Object.entries(completedParts)
      .map(([partNumber, ETag]) => ({ partNumber: Number(partNumber), ETag }))
      .sort((a, b) => a.partNumber - b.partNumber);

    try {
      await apiCall(`/s3/multipart-complete/${bucketName}`, {
        method: 'POST',
        body: JSON.stringify({ uploadId, key, parts: sortedParts, fileSize }),
      });
      removeCheckpoint(bucketName, key); // Req 5.3 — success removes checkpoint
    } catch {
      toast.error('Failed to complete upload');
      // Preserve checkpoint for retry (Req 5.6) — do NOT call removeCheckpoint
    }

    } finally {
      cleanupTransfer();
    }
  };

  return { uploadMultipart };
}
